import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiErrorDetail } from "@/lib/api";
import { getPendingPairing, clearPendingPairing } from "@/lib/guest";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Clapperboard, Loader2 } from "lucide-react";
import { GoogleButton } from "@/components/GoogleButton";
import { toast } from "sonner";

export default function Auth({ mode = "login" }) {
  const isRegister = mode === "register";
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ username: "", email: "", password: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      if (isRegister) {
        await register(form.username, form.email, form.password);
        toast.success("Welcome to DinnerMovie.ai!");
      } else {
        await login(form.email, form.password);
        toast.success("Welcome back!");
      }
      // Carry a guest's last free pairing into their new account
      const pending = getPendingPairing();
      if (pending) {
        try {
          await api.post("/pairings", { ...pending, is_public: false });
          clearPendingPairing();
          toast.success(`Saved your "${pending.movie_title}" pairing to your collection!`);
        } catch {
          clearPendingPairing();
        }
      }
      navigate("/pairings");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center px-5 pt-16"
      data-testid="auth-page"
    >
      <div className="w-full max-w-md animate-fade-up">
        <div className="flex items-center gap-2 mb-8 justify-center">
          <Clapperboard className="w-7 h-7 text-[#E50914]" />
          <span className="font-display text-2xl font-bold">
            DinnerMovie<span className="text-[#E50914]">.ai</span>
          </span>
        </div>

        <div className="rounded-2xl border border-white/10 bg-[#0d0d0d] p-8">
          <h1 className="font-display text-3xl font-bold mb-1">
            {isRegister ? "Create your account" : "Welcome back"}
          </h1>
          <p className="text-white/50 text-sm mb-6">
            {isRegister
              ? "Start pairing movies with unforgettable meals."
              : "Log in to see your pairings."}
          </p>

          <form onSubmit={submit} className="space-y-4">
            {isRegister && (
              <div>
                <Label htmlFor="username" className="text-white/70">Username</Label>
                <Input
                  id="username"
                  name="username"
                  data-testid="auth-username-input"
                  required
                  value={form.username}
                  onChange={onChange}
                  className="mt-1.5 bg-black/40 border-white/10 h-11 rounded-xl"
                  placeholder="cinephile"
                />
              </div>
            )}
            <div>
              <Label htmlFor="email" className="text-white/70">Email</Label>
              <Input
                id="email"
                name="email"
                type="email"
                data-testid="auth-email-input"
                required
                value={form.email}
                onChange={onChange}
                className="mt-1.5 bg-black/40 border-white/10 h-11 rounded-xl"
                placeholder="you@example.com"
              />
            </div>
            <div>
              <Label htmlFor="password" className="text-white/70">Password</Label>
              <Input
                id="password"
                name="password"
                type="password"
                data-testid="auth-password-input"
                required
                value={form.password}
                onChange={onChange}
                className="mt-1.5 bg-black/40 border-white/10 h-11 rounded-xl"
                placeholder="••••••••"
              />
            </div>

            {error && (
              <p className="text-sm text-[#E50914]" data-testid="auth-error">
                {error}
              </p>
            )}

            <Button
              type="submit"
              disabled={loading}
              data-testid="auth-submit-btn"
              className="w-full rounded-full bg-[#E50914] hover:bg-[#c00811] text-white h-11 text-base"
            >
              {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {isRegister ? "Sign Up" : "Log In"}
            </Button>
          </form>

          <div className="flex items-center gap-3 my-5">
            <span className="h-px flex-1 bg-white/10" />
            <span className="text-xs text-white/40 uppercase tracking-widest">or</span>
            <span className="h-px flex-1 bg-white/10" />
          </div>

          <GoogleButton
            label={isRegister ? "Sign up with Google" : "Continue with Google"}
            testid="google-login-btn"
          />

          <p className="text-center text-sm text-white/50 mt-6">
            {isRegister ? (
              <>
                Already have an account?{" "}
                <Link to="/login" className="text-[#E50914] hover:underline" data-testid="auth-switch-login">
                  Log in
                </Link>
              </>
            ) : (
              <>
                New here?{" "}
                <Link to="/register" className="text-[#E50914] hover:underline" data-testid="auth-switch-register">
                  Create an account
                </Link>
              </>
            )}
          </p>
        </div>
      </div>
    </div>
  );
}
